import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@nexply/ui';
import { api } from '../lib/api.js';
import { FloatingField } from '../components/FloatingField.js';
import { BrandSide } from './login.js';
import { LegalFooter } from '../components/LegalFooter.js';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.auth.forgotPassword({ email });
      setSent(true);
    } catch (err) {
      const e = err as { status?: number; message?: string };
      setError(
        e.status === 429
          ? 'Zu viele Versuche — bitte warte ein paar Minuten.'
          : e.message ?? 'Anfrage fehlgeschlagen. Versuche es später erneut.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-dvh w-full overflow-hidden bg-ink lg:grid lg:grid-cols-[1.2fr_1fr] xl:grid-cols-[1.4fr_1fr]">
      <BrandSide />

      <div className="relative z-10 flex min-h-dvh w-full flex-col">
        <div className="lg:hidden pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-gold/15 blur-[120px]" />
        <div className="lg:hidden pointer-events-none absolute bottom-0 right-0 h-[280px] w-[280px] rounded-full bg-gold/10 blur-[100px]" />

        <div className="relative z-10 mx-auto flex w-full max-w-sm flex-1 flex-col px-6 pt-[calc(env(safe-area-inset-top)+48px)] pb-[calc(env(safe-area-inset-bottom)+24px)] lg:max-w-md lg:justify-center lg:py-12 lg:px-10">
          <div className="text-center mb-10 lg:hidden animate-slide-up">
            <h1 className="text-6xl font-semibold tracking-tighter text-gold-grad">nexply</h1>
            <p className="mt-3 text-[11px] uppercase tracking-[0.4em] text-gold/70">Life is a Puzzle</p>
          </div>

          {sent ? (
            <div className="flex flex-1 flex-col gap-4 animate-slide-up lg:flex-none lg:rounded-3xl lg:border lg:border-line lg:bg-surface-1 lg:p-8 lg:shadow-card-lg">
              <h2 className="text-2xl font-semibold tracking-tight text-text">E-Mail unterwegs</h2>
              <p className="text-sm text-text-dim">
                Wenn zu <span className="text-text">{email}</span> ein Konto existiert, haben wir dir
                gerade einen Link zum Zurücksetzen geschickt. Er ist 60 Minuten gültig — schau auch im
                Spam-Ordner nach.
              </p>
              <div className="mt-auto lg:mt-2 pt-8 lg:pt-4 text-center text-sm text-text-dim">
                <Link to="/login" className="font-medium text-gold-light hover:text-gold transition">
                  Zurück zur Anmeldung
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4 animate-slide-up lg:flex-none lg:rounded-3xl lg:border lg:border-line lg:bg-surface-1 lg:p-8 lg:shadow-card-lg">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-text">Passwort vergessen?</h2>
                <p className="mt-1 text-sm text-text-dim">
                  Kein Problem. Wir schicken dir einen Link zum Zurücksetzen.
                </p>
              </div>

              <div className="space-y-3 mt-2">
                <FloatingField
                  id="email"
                  label="E-Mail"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={setEmail}
                  required
                />
              </div>

              {error && (
                <div className="text-xs text-danger bg-danger/10 border border-danger/25 rounded-xl px-4 py-3">
                  {error}
                </div>
              )}

              <Button type="submit" size="lg" fullWidth loading={busy}>
                Link anfordern
              </Button>

              <div className="mt-auto lg:mt-2 pt-8 lg:pt-4 text-center text-sm text-text-dim">
                Doch wieder eingefallen?{' '}
                <Link to="/login" className="font-medium text-gold-light hover:text-gold transition">
                  Einloggen
                </Link>
              </div>
            </form>
          )}
          <LegalFooter className="mt-8 lg:mt-6" />
        </div>
      </div>
    </div>
  );
}
